/**
 * The provider and model assurance register (AIOE 0.40.0; docs/research/2026-10-genesis-and-data3-practice-audit.md, item 3).
 * What we know about where each AI provider's service keeps and uses an organisation's data, as one rating per
 * provider and tier, so a rule on a project's classification (projects.ts) has facts to stand on.
 *
 * Mojo Up reviews the built-in register and corrects it between releases from Mojo Up AI Cloud; an organisation's
 * administrators record their own assessment of a row (what the rating rests on, the evidence, a review-by date)
 * and add providers the register lacks, matched to the workbench's work by `match`
 * (docs/plans/2026-10-assurance-assessments.md). Mirrored by the workbench (shared/schemas/assurance.ts).
 *
 *   GET    /admin/v1/assurance                               administrators: the organisation's register
 *   GET    /admin/v1/assurance/:provider/:tier/assessments   administrators: a row's assessments, newest first
 *   POST   /admin/v1/assurance/:provider/:tier/assessments   administrators: record an assessment
 *   DELETE /admin/v1/assurance/:provider/:tier               administrators: withdraw it, back to Mojo Up's rating
 *   PUT    /admin/v1/assurance/:provider/:tier               administrators: a rating and note alone (older consoles)
 *   POST   /admin/v1/assurance/providers                     administrators: add the organisation's own provider
 *   PATCH  /admin/v1/assurance/providers/:provider/:tier     administrators: change its name, match or note
 *   DELETE /admin/v1/assurance/providers/:provider/:tier     administrators: retire it
 *   POST   /admin/v1/assurance/evidence                      administrators: upload an evidence document
 *   GET    /admin/v1/assurance/evidence/:id                  administrators: download one
 *   PUT    /admin/v1/assurance/register/:provider/:tier      Mojo Up's organisation on Cloud: add or edit a published row
 *   DELETE /admin/v1/assurance/register/:provider/:tier      Mojo Up's organisation on Cloud: retire a published row
 *   GET    /projects/v1/:id/assurance                        members: what the project's classification lets it use
 *   POST   /admin/v1/assurance/check                         administrators, Enterprise: fetch Mojo Up's register now
 *   GET    /assurance/v1/register.json                       public, Cloud: the register Mojo Up publishes
 */
import { z } from "zod";
/** Best first. */
export const assuranceRatingSchema = z.enum(["S", "E", "P", "X"]);
export const ASSURANCE_ORDER = ["S", "E", "P", "X"];
export const ASSURANCE_WORDS = {
    S: "Sovereign-ready",
    E: "Enterprise-assured",
    P: "Protected",
    X: "Exploration only",
};
export const ASSURANCE_DESCRIPTIONS = {
    S: "Data stays in the country or on the machine, under a contract, and is never used to train a model.",
    E: "A business contract, an isolated tenant and no training on inputs; data may be processed offshore.",
    P: "Business terms and no training on inputs, but little control over where data goes or how long it is kept.",
    X: "Fine for public or made-up material; it may keep or learn from what it is given.",
};
/** A host the work goes to: lower case, `*.` in front for any subdomain. */
const hostPatternSchema = z
    .string()
    .trim()
    .toLowerCase()
    .max(253)
    .regex(/^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/, "a host name, such as llm.example.com or *.example.com");
/**
 * What a row applies to in the workbench's work, on the keys the workbench decides by (`classificationVerdict`:
 * the provider or runtime id of the call, the host of the endpoint it goes to, the model). An organisation's own
 * provider always has one; a Mojo Up row may (one the workbench has no built-in key for).
 */
export const assuranceMatchSchema = z.object({
    /** Workbench provider or runtime ids: azure, bedrock, openai-compatible, vllm, claude-code. At least one. */
    providers: z
        .array(z
        .string()
        .trim()
        .min(1)
        .max(100)
        .regex(/^[A-Za-z0-9._-]+$/, "a provider id, such as azure or openai-compatible"))
        .min(1)
        .max(20),
    /** The endpoint's host; `*.example.com` for any subdomain. Left out: any host. */
    hosts: z.array(hostPatternSchema).max(20).optional(),
    /** Model ids, `*` a wildcard. Left out: any model. */
    models: z.array(z.string().trim().min(1).max(200)).max(50).optional(),
});
const globToRegExp = (glob) => new RegExp(`^${glob.split("*").map((s) => s.replace(/[.+?^${}()|[\]\\]/g, "\\$&")).join(".*")}$`, "i");
/**
 * Whether a call (its provider or runtime id, the endpoint's host, the model) is what `match` names. Case does not
 * matter; `*.example.com` is any subdomain of example.com, not example.com itself; an unknown host or model fits
 * no match that names hosts or models.
 */
export function assuranceMatches(match, route) {
    if (!match.providers.some((p) => p.toLowerCase() === route.providerId.toLowerCase()))
        return false;
    const host = (route.host ?? "").toLowerCase();
    if (match.hosts?.length && !match.hosts.some((h) => (h.startsWith("*.") ? host.length > h.length - 1 && host.endsWith(h.slice(1)) : host === h)))
        return false;
    const model = route.model ?? "";
    // An unknown model fits no row that names models, not even "*".
    if (match.models?.length && (!model || !match.models.some((m) => globToRegExp(m).test(model))))
        return false;
    return true;
}
/** How specific a match is, to choose between two that fit: a host counts 2, a model 1. */
export const assuranceMatchWeight = (match) => (match.hosts?.length ? 2 : 0) + (match.models?.length ? 1 : 0);
export const providerAssuranceSchema = z.object({
    /** The provider ids the platform uses: anthropic, openai, azure-openai, google, github-copilot, local. */
    provider: z.string().min(1).max(100),
    /** consumer, team, enterprise, api or local. */
    tier: z.string().min(1).max(50),
    rating: assuranceRatingSchema,
    /** au, us, eu, local or unknown: where the data is processed. */
    region: z.string().min(1).max(20),
    trainsOnInputs: z.boolean(),
    tenantIsolation: z.boolean(),
    /** Days the provider keeps inputs; null is unknown, 0 is zero retention. */
    retentionDays: z.number().int().min(0).nullable(),
    /** A contract (not just terms of use) binds the provider. */
    contract: z.boolean(),
    /** What the rating depends on. */
    note: z.string().max(300),
    reviewedAt: z.string(),
    /** A name to show, for a row the console has no word for (an organisation's own provider, a row Mojo Up added). */
    name: z.string().max(100).optional(),
    /** What it applies to in the workbench's work, beyond the workbench's own ids for the built-in rows. */
    match: assuranceMatchSchema.optional(),
    /** In a published register: Mojo Up has retired the row, and a deployment drops it. */
    retired: z.boolean().optional(),
});
export const assuranceRegisterSchema = z.object({
    version: z.string(),
    reviewedAt: z.string(),
    entries: z.array(providerAssuranceSchema),
});
/** The tiers a row may have. */
export const ASSURANCE_TIERS = ["consumer", "team", "enterprise", "api", "local"];
export const assuranceTierSchema = z.enum(ASSURANCE_TIERS);
/** Names for the built-in rows' providers, and words for the tiers, as people read them. */
export const ASSURANCE_PROVIDER_NAMES = { anthropic: "Anthropic", openai: "OpenAI", "azure-openai": "Azure OpenAI", google: "Google", "github-copilot": "GitHub Copilot", local: "Local models" };
export const ASSURANCE_TIER_WORDS = { consumer: "personal plans", team: "teams", enterprise: "enterprise", api: "API", local: "on your own machines" };
/** A row's name: its own, else the provider's, else its id. */
export const assuranceRowName = (r) => r.name || ASSURANCE_PROVIDER_NAMES[r.provider] || r.provider;
/** Where data is processed or stored, as an assessment answers it. */
export const assessmentRegionSchema = z.enum(["au", "nz", "us", "eu", "uk", "local", "other", "unknown"]);
const words = () => z.string().trim().max(500).default("");
/**
 * What an assessment rests on, criterion by criterion, as the register's rows are rated (where data is processed
 * and stored, training on inputs, retention, the contract, isolation): each a choice and the assessor's own words.
 */
export const assuranceBasisSchema = z.object({
    residency: z.object({ processedIn: assessmentRegionSchema.default("unknown"), storedIn: assessmentRegionSchema.default("unknown"), words: words() }).prefault({}),
    training: z.object({ answer: z.enum(["no", "yes", "unknown"]).default("unknown"), words: words() }).prefault({}),
    retention: z.object({ days: z.number().int().min(0).max(36_500).nullable().default(null), words: words() }).prefault({}),
    terms: z.object({ answer: z.enum(["contract", "business-terms", "personal-terms", "unknown"]).default("unknown"), words: words() }).prefault({}),
    isolation: z.object({ answer: z.enum(["isolated", "shared", "unknown"]).default("unknown"), words: words() }).prefault({}),
});
/** The basis nobody has answered: an override recorded before assessments, or a rating given alone. */
export const UNKNOWN_BASIS = assuranceBasisSchema.parse({});
/** An uploaded evidence document's id: the sha-256 of its bytes. */
export const assuranceEvidenceIdSchema = z.string().regex(/^[0-9a-f]{64}$/);
/** The kinds of document evidence may be. */
export const ASSURANCE_EVIDENCE_TYPES = [
    "application/pdf",
    "text/plain",
    "image/png",
    "image/jpeg",
    "image/webp",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
export const assuranceEvidenceTypeSchema = z.enum(ASSURANCE_EVIDENCE_TYPES);
export const ASSURANCE_EVIDENCE_MAX_BYTES = 10 * 1024 * 1024;
const isHttps = (u) => {
    try {
        const url = new URL(u);
        return url.protocol === "https:" && !!url.hostname;
    }
    catch {
        return false;
    }
};
/** One piece of evidence: a link (https), or a document uploaded to the organisation's evidence. */
export const assuranceEvidenceSchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("link"), url: z.string().trim().max(2000).refine(isHttps, "a link starting https://"), label: z.string().trim().max(200).default("") }),
    z.object({ kind: z.literal("document"), id: assuranceEvidenceIdSchema, name: z.string().trim().min(1).max(200), contentType: assuranceEvidenceTypeSchema, size: z.number().int().min(1).max(ASSURANCE_EVIDENCE_MAX_BYTES) }),
]);
/** A date, YYYY-MM-DD. */
const daySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, "a date, YYYY-MM-DD")
    .refine((d) => !Number.isNaN(Date.parse(`${d}T00:00:00Z`)), "a real date");
/** `POST /admin/v1/assurance/:provider/:tier/assessments`: an organisation's assessment of a provider and tier. */
export const assuranceAssessmentInputSchema = z.object({
    rating: assuranceRatingSchema,
    basis: assuranceBasisSchema.prefault({}),
    evidence: z.array(assuranceEvidenceSchema).max(10).default([]),
    /** When it is to be looked at again; left out, it is never flagged. */
    reviewBy: daySchema.optional(),
    note: z.string().trim().max(500).default(""),
});
/** An assessment as kept: append-only; the newest of a row is its current one, unless that one is withdrawn. */
export const assuranceAssessmentSchema = assuranceAssessmentInputSchema.extend({
    id: z.string().min(1).max(100),
    provider: z.string().min(1).max(100),
    tier: z.string().min(1).max(50),
    assessedBy: z.string().min(1).max(300),
    /** The assessor's name or email as their sign-in gave it, to show. */
    assessedByName: z.string().max(300).optional(),
    assessedAt: z.string(),
    withdrawnAt: z.string().optional(),
    withdrawnBy: z.string().max(300).optional(),
    /** Recorded as an override before assessments: a rating and a note, nothing more. */
    legacy: z.boolean().optional(),
});
/** Whether an assessment's review-by date has passed (the end of that day, UTC, is behind `now`). */
export const assessmentReviewDue = (a, now) => !!a.reviewBy && Date.parse(`${a.reviewBy}T23:59:59.999Z`) < now;
/** The provider id an organisation's own provider takes: `org:` and a slug of its name, never one of Mojo Up's. */
export const ORGANISATION_PROVIDER_PREFIX = "org:";
export const organisationProviderIdSchema = z.string().regex(/^org:[a-z0-9]([a-z0-9-]{0,60}[a-z0-9])?$/);
/** `POST /admin/v1/assurance/providers`: a provider the register lacks, with its first assessment. */
export const assuranceProviderInputSchema = z.object({
    name: z.string().trim().min(1).max(100),
    tier: assuranceTierSchema,
    match: assuranceMatchSchema,
    note: z.string().trim().max(300).default(""),
    assessment: assuranceAssessmentInputSchema,
});
/** `PATCH /admin/v1/assurance/providers/:provider/:tier`. */
export const assuranceProviderPatchSchema = z.object({ name: z.string().trim().min(1).max(100).optional(), match: assuranceMatchSchema.optional(), note: z.string().trim().max(300).optional() });
/** A provider id Mojo Up may publish: lower case, never `org:`. */
export const registerProviderIdSchema = z.string().regex(/^[a-z0-9]([a-z0-9.-]{0,98}[a-z0-9])?$/);
/**
 * `PUT /admin/v1/assurance/register/:provider/:tier`, Mojo Up's organisation on Cloud only: a published row, whole.
 * Putting a retired row brings it back.
 */
export const assuranceRegisterEditSchema = providerAssuranceSchema.omit({ provider: true, tier: true, reviewedAt: true, retired: true });
/** A row as an administrator sees it: the built-in values, with the organisation's override applied and marked. */
export const assuranceRowSchema = providerAssuranceSchema.extend({
    overridden: z.boolean().default(false),
    /** Mojo Up's rating, when the organisation has overridden it. */
    builtInRating: assuranceRatingSchema.optional(),
    overriddenBy: z.string().optional(),
    overriddenAt: z.string().optional(),
    /** Mojo Up changed or added this row after this release was built: it comes from Mojo Up's published register. */
    updatedAfterRelease: z.boolean().optional(),
    /** The rating built into this release, when Mojo Up's published register rates the row differently. */
    releaseRating: assuranceRatingSchema.optional(),
    /** Whose row it is: built into this release, added or changed by Mojo Up since, or the organisation's own provider. */
    origin: z.enum(["built-in", "mojo-up", "organisation"]).optional(),
    /** The organisation's current assessment of the row, when it has one. */
    assessment: assuranceAssessmentSchema.optional(),
    /** The current assessment's review-by date has passed. */
    reviewDue: z.boolean().optional(),
    /** How many assessments the row has had, the current one among them. */
    assessments: z.number().int().min(0).optional(),
});
export const assuranceOverridePutSchema = z.object({ rating: assuranceRatingSchema, note: z.string().max(300).default("") });
/**
 * The register Mojo Up publishes from Mojo Up AI Cloud between releases (docs/plans/2026-10-update-management.md,
 * "The assurance register"): the built-in rows with Mojo Up's own corrections, at
 * `GET /assurance/v1/register.json` (public, ETag). An Enterprise AIOE fetches it with its release sync
 * and uses it as the base under its organisations' overrides.
 */
export const ASSURANCE_REGISTER_PATH = "/assurance/v1/register.json";
export const assuranceRegisterDocumentSchema = z.object({
    version: z.literal(1),
    publishedAt: z.string(),
    /** Goes up by one each time the published rows change. */
    revision: z.number().int().min(0),
    rows: z.array(assuranceRowSchema).max(500),
    notes: z.string().max(2000).optional(),
});
/** Where the base under an organisation's overrides comes from, for the console's one sentence. */
export const assuranceRegisterSourceSchema = z.object({
    /** built-in: this release's own; cloud: Mojo Up AI Cloud's published register (served here); enterprise-mirror: fetched from Mojo Up AI Cloud. */
    kind: z.enum(["built-in", "cloud", "enterprise-mirror"]),
    revision: z.number().int().min(0),
    publishedAt: z.string(),
    /** When the current mirror was fetched. */
    fetchedAt: z.string().optional(),
    /** The last check that reached Mojo Up, whether or not the register had changed. */
    checkedAt: z.string().optional(),
    /** Where an Enterprise fetches the register from. */
    from: z.string().optional(),
    /** Why the last check failed; the last mirror (or the built-in register) stays in use. */
    lastError: z.string().optional(),
    /** Cloud: this organisation publishes the register (its overrides are what every deployment receives). */
    publisher: z.boolean().optional(),
});
/** The least assurance a classification allows: public takes anything, internal needs P or better, confidential E or better, restricted S (Sovereign-ready). Unclassified is not restricted. */
export function minimumRatingFor(classification) {
    return classification === "restricted" ? "S" : classification === "confidential" ? "E" : classification === "internal" ? "P" : "X";
}
export const ratingMeets = (rating, minimum) => ASSURANCE_ORDER.indexOf(rating) <= ASSURANCE_ORDER.indexOf(minimum);
/** One sentence saying why a provider and tier is or is not allowed at a minimum rating. */
export function assuranceWhy(rating, minimum, allowed, classified) {
    if (!classified)
        return `${ASSURANCE_WORDS[rating]}; the project has no classification, so nothing is held back.`;
    return allowed
        ? `${ASSURANCE_WORDS[rating]} meets the ${ASSURANCE_WORDS[minimum]} floor this classification sets.`
        : `${ASSURANCE_WORDS[rating]} is below the ${ASSURANCE_WORDS[minimum]} floor this classification sets.`;
}
//# sourceMappingURL=assurance.js.map